import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { notifyAdmins } from "@/lib/notify.server";

export const submitReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      placeSlug: z.string().trim().min(1).max(160),
      rating: z.number().int().min(1).max(5),
      comment: z.string().trim().max(1000).default(""),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;

    const { error } = await supabaseAdmin
      .from("place_reviews")
      .upsert(
        {
          user_id: userId,
          place_slug: data.placeSlug,
          rating: data.rating,
          comment: data.comment,
          // Every new or edited review goes back through moderation.
          status: "pending",
          reject_reason: null,
          moderated_at: null,
          moderated_by: null,
        },
        { onConflict: "user_id,place_slug" },
      );

    if (error) throw new Error(error.message);

    // Notify admins about new review
    try {
      const preview = data.comment.length > 100 ? `${data.comment.slice(0, 100)}…` : data.comment;
      await notifyAdmins({
        title: `Review awaiting approval — ${data.rating} stars`,
        body: preview || `a user left a ${data.rating}-star review`,
        link: `/admin?page=reviews`,
        kind: "review",
      });
    } catch (e) {
      console.error("[review] notifyAdmins failed:", e);
    }

    return { ok: true, status: "pending" as const };
  });

export const deleteMyReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ placeSlug: z.string().trim().min(1).max(160) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { error } = await supabaseAdmin
      .from("place_reviews")
      .delete()
      .eq("user_id", userId)
      .eq("place_slug", data.placeSlug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
